import { Engine, GameState, ItemId } from './index';
import { InputManager } from './input/InputManager';
import {
  formatGamepadBinding,
  formatKeyCode,
  formatKeyboardBinding,
  formatMouseButton,
  loadControlSettings,
} from './input/bindings';
import {
  loadGraphicsSettings,
  saveGraphicsSettings,
  type GraphicsQuality,
} from './core/graphicsSettings';
import {
  createAndHostWorld,
  fetchWorldList,
  hostExistingWorld,
  joinWorld,
} from './network/MultiplayerClient';
import { MainMenu } from './ui/MainMenu';
import { SettingsUI } from './ui/SettingsUI';

const appEl = document.getElementById('app');
const hud = document.getElementById('hud');
const hpPanel = document.getElementById('hp-panel');
const hurtVignette = document.getElementById('hurt-vignette');

if (!appEl) {
  throw new Error('#app root missing');
}
const app: HTMLElement = appEl;

const PLAYER_ID_KEY = 'minelunky.playerId';

function getOrCreatePlayerId(): string {
  const existing = localStorage.getItem(PLAYER_ID_KEY);
  if (existing) return existing;
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  localStorage.setItem(PLAYER_ID_KEY, id);
  return id;
}

/** Pre-game input + settings (no canvas yet). */
const menuInputHost = document.createElement('div');
menuInputHost.style.display = 'none';
app.appendChild(menuInputHost);
const menuInput = new InputManager(menuInputHost);
let menuSettingsRaf = 0;
const menuSettings = new SettingsUI(app, {
  input: menuInput,
  requestPointerLock: () => undefined,
  exitPointerLock: () => undefined,
  getGraphicsQuality: () => loadGraphicsSettings().quality,
  setGraphicsQuality: (quality: GraphicsQuality) => {
    saveGraphicsSettings({ quality });
    engine?.setGraphicsQuality(quality);
  },
  onClosed: () => {
    if (menuSettingsRaf) {
      cancelAnimationFrame(menuSettingsRaf);
      menuSettingsRaf = 0;
    }
  },
});

function openMenuControls(): void {
  menuSettings.setOpen(true);
  let last = performance.now();
  const tick = (now: number): void => {
    if (!menuSettings.isOpen) {
      menuSettingsRaf = 0;
      return;
    }
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    menuInput.update(dt);
    menuSettingsRaf = requestAnimationFrame(tick);
  };
  if (menuSettingsRaf) cancelAnimationFrame(menuSettingsRaf);
  menuSettingsRaf = requestAnimationFrame(tick);
}

const menu = new MainMenu(app, {
  onOpenControls: () => openMenuControls(),
});
const playerId = getOrCreatePlayerId();

let toastMessage = '';
let toastUntil = 0;
let engine: Engine | null = null;
let hurtPulseTimer = 0;

function controlHint(usingPad: boolean, usingTouch = false): string {
  const s = engine?.getInput().getSettings() ?? loadControlSettings();
  if (usingTouch) {
    return 'Tap Forge to craft upgrades';
  }
  if (usingPad) {
    const primary = formatGamepadBinding(s.gamepad.buttons.primary);
    const inv = formatGamepadBinding(s.gamepad.buttons.inventory);
    const interact = formatGamepadBinding(s.gamepad.buttons.interact);
    return `Stick move/look · ${primary} mine/attack · ${inv} forge · ${interact} shop · Esc/Start pause · Controls in pause`;
  }
  const inv = formatKeyboardBinding(s.keyboard.inventory);
  const interact = formatKeyboardBinding(s.keyboard.interact);
  const primary = formatMouseButton(s.mouse.primaryButton);
  const h1 = s.keyboard.hotbar1.map(formatKeyCode).join('/') || '1';
  const h2 = s.keyboard.hotbar2.map(formatKeyCode).join('/') || '2';
  return `${h1} pickaxe · ${h2} sword · ${primary} foes · Hold ${primary} mine · ${inv} forge · ${interact} shop`;
}

function refreshHud(state = engine?.gameState): void {
  if (!hud || !engine || state === undefined) return;
  const inv = engine.activeInventory;
  const stage = Math.max(1, inv.currentLevel);
  const bankIron = engine.inventory.getBankAmount(ItemId.IronOre);
  const bankGold = engine.inventory.getBankAmount(ItemId.GoldOre);
  const hp = engine.getPlayerHealth();
  const foes = engine.getEnemyCount();
  const room = engine.roomCode;
  const role = engine.multiplayerRole;
  const usingPad = engine.getInput().isUsingGamepad();
  const usingTouch = engine.getInput().isUsingTouch();
  const s = engine.getInput().getSettings();

  let hint: string;
  if (state === GameState.Respawning) {
    const confirm = usingPad
      ? formatGamepadBinding(s.gamepad.buttons.confirm)
      : formatKeyboardBinding(s.keyboard.confirm);
    hint = `You died — Respawn (${confirm})`;
  } else if (state === GameState.Paused) {
    hint = 'Paused — Resume or open Controls';
  } else if (engine.isDevMenuOpen()) {
    hint = 'Dev menu — jump to a stage · ` to close';
  } else if (engine.isShopOpen()) {
    const close = usingTouch
      ? '✕ or tap outside'
      : usingPad
        ? formatGamepadBinding(s.gamepad.buttons.interact)
        : `${formatKeyboardBinding(s.keyboard.pause)} / ${formatKeyboardBinding(s.keyboard.interact)}`;
    hint = `Shop open — pick amounts to sell · ${close} to close`;
  } else if (engine.isInventoryOpen()) {
    const close = usingTouch
      ? 'Close'
      : usingPad
        ? formatGamepadBinding(s.gamepad.buttons.inventory)
        : formatKeyboardBinding(s.keyboard.inventory);
    hint = `Forge open — ${close} to close`;
  } else if (engine.isNearShop()) {
    const interact = usingTouch
      ? 'Shop'
      : usingPad
        ? formatGamepadBinding(s.gamepad.buttons.interact)
        : formatKeyboardBinding(s.keyboard.interact);
    hint = usingTouch
      ? `Shop — tap ${interact} to sell run loot`
      : `Shop — ${interact} to sell run loot`;
  } else if (engine.isNearExitDoor()) {
    if (engine.isBossExitLocked()) {
      hint = 'Exit locked — defeat the boss to unlock the exit';
    } else if (role === 'client') {
      hint = 'Exit door — wait for everyone, then the host steps through';
    } else if (room && !engine.canAdvanceExitDoor()) {
      hint = 'Exit door — wait for your teammate';
    } else {
      hint = 'Exit door — step through to go deeper (loot stays in run bag)';
    }
  } else {
    hint = controlHint(usingPad, usingTouch);
  }

  const minePct = Math.round(engine.getMiningFraction() * 100);
  const toast = performance.now() < toastUntil && toastMessage ? toastMessage : null;

  let lines: string[];
  if (usingTouch) {
    // Keep the mobile HUD to a few short lines so landscape stays playable.
    const held = engine.getHeldToolName();
    lines = [
      room ? `${role ?? 'online'} ${room}` : null,
      `S${stage} · ${foes} foe${foes === 1 ? '' : 's'} · ${held}`,
      minePct > 0 ? `Mining ${minePct}%` : null,
      `${inv.dirt}d ${inv.stone}s ${inv.iron}i ${inv.gold}g · ${engine.profile.walletCredits}¢`,
      // Only show contextual hints (shop/exit/death/toast), not the permanent control blurb.
      hint === 'Tap Forge to craft upgrades' ? null : hint,
      toast,
    ].filter((line): line is string => !!line);
  } else {
    lines = [
      room ? `Online ${role ?? ''} · Code ${room}` : 'Singleplayer',
      usingPad ? 'Input: Controller' : 'Input: Keyboard',
      `State: ${state}`,
      `Stage: ${stage}`,
      `Enemies: ${foes}`,
      `Held: ${engine.getHeldToolName()}`,
      minePct > 0 ? `Mining: ${minePct}%` : null,
      `Run: dirt ${inv.dirt} · stone ${inv.stone} · iron ${inv.iron} · gold ${inv.gold}`,
      `Bank ores: iron ${bankIron} · gold ${bankGold} · Wallet ${engine.profile.walletCredits}¢`,
      hint,
      toast,
    ].filter((line): line is string => !!line);
  }
  hud.textContent = lines.join('\n');
  hud.style.whiteSpace = 'pre-wrap';
  refreshHpPanel(hp);
}

function hpFillColor(frac: number): string {
  const r = frac > 0.5 ? Math.round(255 * (1 - (frac - 0.5) * 2)) : 255;
  const g = frac > 0.5 ? 200 : Math.round(200 * (frac / 0.5));
  return `rgb(${r},${g},48)`;
}

function refreshHpPanel(local: { health: number; maxHealth: number }): void {
  if (!hpPanel || !engine) return;
  const remotes = engine.getRemoteHealth();
  const rows: Array<{ name: string; health: number; maxHealth: number; you?: boolean }> = [
    {
      name: engine.profile.displayName || 'You',
      health: local.health,
      maxHealth: local.maxHealth,
      you: true,
    },
    ...remotes.map((r) => ({
      name: r.displayName,
      health: r.health,
      maxHealth: r.maxHealth,
    })),
  ];

  hpPanel.innerHTML = rows
    .map((row) => {
      const max = Math.max(1, row.maxHealth);
      const frac = Math.max(0, Math.min(1, row.health / max));
      const labelClass = row.you ? 'hp-label you' : 'hp-label';
      const title = row.you ? `${row.name} (you)` : row.name;
      return `<div class="hp-row">
        <div class="${labelClass}"><span>${escapeHud(title)}</span><span>${Math.round(row.health)}/${Math.round(max)}</span></div>
        <div class="hp-track"><div class="hp-fill" style="transform:scaleX(${frac});background:${hpFillColor(frac)}"></div></div>
      </div>`;
    })
    .join('');
}

function escapeHud(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function pulseHurtFeedback(): void {
  if (hurtPulseTimer) window.clearTimeout(hurtPulseTimer);
  hurtVignette?.classList.add('active');
  hpPanel?.classList.add('hurt');
  hurtPulseTimer = window.setTimeout(() => {
    hurtVignette?.classList.remove('active');
    hpPanel?.classList.remove('hurt');
    hurtPulseTimer = 0;
  }, 220);
}

function disposeMenuInput(): void {
  menuSettings.setOpen(false);
  if (menuSettingsRaf) {
    cancelAnimationFrame(menuSettingsRaf);
    menuSettingsRaf = 0;
  }
  menuSettings.dispose();
  menuInput.dispose();
  menuInputHost.remove();
}

function wireEngine(next: Engine): void {
  engine = next;
  next.events.on('state:changed', ({ next: state }) => refreshHud(state));
  next.events.on('level:entered', () => refreshHud());
  next.events.on('block:mined', () => refreshHud());
  next.events.on('inventory:changed', () => refreshHud());
  next.events.on('player:damaged', () => {
    refreshHud();
    pulseHurtFeedback();
  });
  next.events.on('player:healed', () => refreshHud());
  next.events.on('player:respawned', () => refreshHud());
  next.events.on('network:remote-move', () => refreshHud());
  next.events.on('enemy:killed', ({ name }) => {
    toastMessage = `${name} slain`;
    toastUntil = performance.now() + 1600;
    refreshHud();
  });
  next.events.on('enemy:spawned', ({ count, stage }) => {
    if (stage > 0 && stage % 5 === 0) {
      toastMessage = count > 0 ? `Boss fight — ${count} foe${count === 1 ? '' : 's'}` : 'Boss arena';
      toastUntil = performance.now() + 2800;
    } else if (count > 0) {
      toastMessage = `${count} cave creature${count === 1 ? '' : 's'} nearby`;
      toastUntil = performance.now() + 2500;
    }
    refreshHud();
  });
  next.events.on('boss:defeated', ({ name }) => {
    toastMessage = `${name} defeated — exit unlocked`;
    toastUntil = performance.now() + 2800;
    refreshHud();
  });
  next.events.on('ui:toast', ({ message }) => {
    toastMessage = message;
    toastUntil = performance.now() + 3500;
    refreshHud();
  });
  next.events.on('run:extracted', ({ level }) => {
    console.info(`[banked] stage ${level}`);
    refreshHud();
  });
  next.events.on('shop:sold', ({ itemId, amount, credits }) => {
    console.info(`[shop] sold ${amount}× ${itemId} for ${credits}¢`);
    refreshHud();
  });
  next.events.on('world:ready', ({ seed }) => {
    console.info(`[world] seed=0x${seed.toString(16)}`);
  });
  next.events.on('loot:dropped', ({ itemCount, stage }) => {
    console.info(`[loot] dropped ${itemCount} items on stage ${stage}`);
  });
  next.events.on('loot:recovered', ({ itemCount }) => {
    console.info(`[loot] recovered ${itemCount} items`);
    refreshHud();
  });
  next.events.on('player:death', ({ cause, impactSpeed }) => {
    console.info(`[death] ${cause} impact=${impactSpeed.toFixed(1)} — loot bag left behind`);
    refreshHud();
  });
  next.events.on('inventory:reset', () => refreshHud());
  next.events.on('network:session', ({ code, role }) => {
    toastMessage =
      role === 'host'
        ? `Hosting — share code ${code}`
        : `Joined room ${code}`;
    toastUntil = performance.now() + 5000;
    refreshHud();
  });
}

async function boot(): Promise<void> {
  // Loop until a session starts successfully (failed online attempts return to menu).
  for (;;) {
    menu.setStatus('');
    const choice = await menu.choose(() => fetchWorldList());

    const nextEngine = new Engine({
      canvasParent: app,
      playerId,
      displayName: choice.displayName,
    });
    wireEngine(nextEngine);

    try {
      if (choice.mode === 'singleplayer') {
        menu.hide();
        disposeMenuInput();
        nextEngine.start({ continueSave: choice.continueSave });
        refreshHud();
        break;
      }

      menu.setStatus(
        choice.mode === 'join' ? 'Joining…' : 'Starting host session…',
      );

      const session =
        choice.mode === 'host-new'
          ? await createAndHostWorld({
              name: choice.worldName,
              playerId,
              displayName: choice.displayName,
            })
          : choice.mode === 'host-existing'
            ? await hostExistingWorld({
                code: choice.code,
                playerId,
                displayName: choice.displayName,
              })
            : await joinWorld({
                code: choice.code,
                playerId,
                displayName: choice.displayName,
              });

      menu.hide();
      disposeMenuInput();
      nextEngine.startMultiplayer(session);
      refreshHud();
      break;
    } catch (err) {
      nextEngine.dispose();
      engine = null;
      menu.setStatus(err instanceof Error ? err.message : 'Could not start session.');
    }
  }
}

void boot();

setInterval(() => {
  if (!engine) return;
  if (toastMessage && performance.now() >= toastUntil) {
    toastMessage = '';
    refreshHud();
  } else if (
    engine.isNearExitDoor() ||
    engine.isNearShop() ||
    engine.getMiningFraction() > 0 ||
    engine.getEnemyCount() > 0 ||
    engine.roomCode
  ) {
    refreshHud();
  }
}, 100);

declare global {
  interface Window {
    minelunky: Engine | null;
  }
}
Object.defineProperty(window, 'minelunky', {
  get: () => engine,
});
